import { useAuth } from "@flaminem/authentication";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate } from "react-router-dom";

import { useBrandConfig } from "src/brand/config";
import LogoutIcon from "src/core/assets/images/icons/logout.svg?react";
import { SearchFolder } from "src/core/components/custom-ui/search-folder";
import { Spinner } from "src/core/components/custom-ui/spinner";
import { TenantSelect } from "src/core/components/header/TenantSelect";
import { Button } from "src/core/components/ui/button";
import { ROUTE_PATH } from "src/core/constants/routePath";
import { useTenant } from "src/core/contexts/TenantContext/TenantProvider";
import { useEnrichTenantNames } from "src/core/hooks/header/useEnrichTenantNames";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { useToast } from "src/core/hooks/use-toast";
import { TENANT_STATUS } from "src/core/types/tenant";

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { singOutRedirect } = useAuth();

  const navigate = useNavigate();
  const brandConfig = useBrandConfig();
  const paths = useRequiredTenantPaths();

  const { tenantId, availableTenants, status } = useTenant();

  const tenantHomePath = tenantId ? generatePath(ROUTE_PATH.TENANT_ROOT, { tenantId }) : "/";

  /**
   * Changement de tenant via la liste déroulante.
   * On redirige vers la page d'accueil du nouveau tenant.
   */
  const handleTenantChange = useCallback(
    (newTenantId: string) => {
      navigate(paths.home(newTenantId));
      toast({ description: t("tenant.switchSuccess") });
    },
    [navigate, paths, toast, t]
  );

  const { tenants: enrichedTenants, isLoading: isEnrichingTenants } =
    useEnrichTenantNames(availableTenants);

  const LogoComponent = brandConfig.logo as React.ComponentType<React.SVGProps<SVGSVGElement>>;

  const handleClientSelect = useCallback(
    (folderId: string) => {
      navigate(paths.folder(folderId));
    },
    [navigate, paths]
  );

  const handleLogout = useCallback(() => {
    navigate(paths.home());
    singOutRedirect();
  }, [navigate, singOutRedirect, paths]);

  return (
    <div className="header-container relative justify-between">
      <Link to={tenantHomePath} aria-label={t("home")}>
        <LogoComponent />
      </Link>

      <div className="flex items-center gap-4">
        <div className="relative z-50">
          <SearchFolder
            onSelect={handleClientSelect}
            disabled={isEnrichingTenants || status === TENANT_STATUS.SWITCHING}
          />
        </div>
      </div>
      <div className="flex items-center gap-20">
        {enrichedTenants.length > 1 && (
          <div className="flex items-center gap-4">
            {isEnrichingTenants ? (
              <div
                className="flex w-[200px] items-center justify-center border border-border-secondary rounded-button p-10"
                aria-busy="true"
              >
                <Spinner />
              </div>
            ) : (
              <TenantSelect
                tenantId={tenantId}
                tenants={enrichedTenants}
                onChange={handleTenantChange}
                isSwitching={status === TENANT_STATUS.SWITCHING}
              />
            )}
          </div>
        )}

        <div className="flex items-center gap-4">
          <Button
            data-testid="logout-btn"
            type="button"
            variant="linkDestructive"
            size="small"
            onClick={handleLogout}
            className="mr-[40px]"
          >
            <LogoutIcon />
            {t("logout")}
          </Button>
        </div>
      </div>
    </div>
  );
};
